import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import * as api from "./api";
import * as db from "./db";
import * as sync from "./sync";
import type { Card, Lang, LangProfile, Note, Source } from "./types";
import { DEFAULT_PROFILES, LANGS } from "./types";
import { freshCounts, today, type DailyCounts } from "./scheduler";

interface Collection {
  notes: Note[];
  cards: Card[];
  sources: Source[];
}

interface StoreValue extends Collection {
  ready: boolean;
  lang: Lang;
  setLang: (lang: Lang) => void;
  profile: LangProfile;
  profiles: Record<Lang, LangProfile>;
  saveProfile: (lang: Lang, profile: LangProfile) => Promise<void>;
  counts: DailyCounts;
  bumpCounts: (kind: "new" | "review") => void;
  /** Re-read the local mirror after a write. */
  reload: () => Promise<void>;
  /** Push the outbox and pull changes, then reload. */
  syncNow: () => Promise<void>;
  syncing: boolean;
  syncError: string | null;
  pending: number;
  lastSync: number | null;
  online: boolean;
  /** The deployment is passphrase-gated and this browser has not unlocked it. */
  locked: boolean;
  unlock: () => Promise<void>;
}

const StoreContext = createContext<StoreValue | null>(null);

const LANG_KEY = "langlearn.lang";

export function StoreProvider({ children }: { children: ReactNode }) {
  const [collection, setCollection] = useState<Collection>({ notes: [], cards: [], sources: [] });
  const [ready, setReady] = useState(false);
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem(LANG_KEY);
    return stored === "fr" || stored === "es" ? stored : "fr";
  });
  const [profiles, setProfiles] = useState<Record<Lang, LangProfile>>(DEFAULT_PROFILES);
  // Counters are per language: the two languages have separate daily
  // allowances, so a long French session must not consume the Spanish one.
  const [allCounts, setAllCounts] = useState<Record<Lang, DailyCounts>>(() => ({
    fr: freshCounts(),
    es: freshCounts(),
  }));
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [locked, setLocked] = useState(false);
  const syncingRef = useRef(false);

  const reload = useCallback(async () => {
    const [notes, cards, sources, pendingNow] = await Promise.all([
      db.loadNotes(), db.loadCards(), db.loadSources(), sync.pendingCount(),
    ]);
    setCollection({ notes, cards, sources });
    setPending(pendingNow);
    setLastSync(await sync.lastSyncedAt());
  }, []);

  // Any request coming back locked flips the whole app to the gate, rather
  // than surfacing a sync error the passphrase would actually fix.
  useEffect(() => {
    api.setLockedHandler(() => setLocked(true));
  }, []);

  const doSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    setSyncError(null);
    try {
      await sync.syncNow();
      await reload();
      const loaded = {} as Record<Lang, LangProfile>;
      for (const l of LANGS) loaded[l] = await sync.loadProfile(l);
      setProfiles(loaded);
    } catch (err) {
      if (!(err instanceof api.LockedError)) {
        setSyncError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [reload]);

  const unlock = useCallback(async () => {
    setLocked(false);
    await doSync();
  }, [doSync]);

  // Boot: show the local mirror immediately, then sync in the background. The
  // app is usable before the network answers, which is the point of the mirror.
  useEffect(() => {
    (async () => {
      const loaded = {} as Record<Lang, LangProfile>;
      for (const l of LANGS) loaded[l] = await sync.loadProfile(l);
      setProfiles(loaded);
      const storedCounts = {} as Record<Lang, DailyCounts>;
      for (const l of LANGS) {
        storedCounts[l] = await db.getMeta<DailyCounts>(`counts:${l}`, freshCounts());
      }
      setAllCounts(storedCounts);
      await reload();
      setReady(true);
      if (!navigator.onLine) return;
      // Establish whether this deployment is gated before anything else tries
      // an API call, so the first thing shown is the gate rather than an error.
      try {
        const session = await api.getSession();
        if (session.required && !session.authenticated) {
          setLocked(true);
          return;
        }
      } catch {
        // Offline or unreachable; the local mirror still works.
      }
      void doSync();
    })();
  }, [reload, doSync]);

  // Daily counters reset at local midnight, not on a rolling 24 hours.
  useEffect(() => {
    const stale = LANGS.filter((l) => allCounts[l].date !== today());
    if (stale.length === 0) return;
    setAllCounts((current) => {
      const next = { ...current };
      for (const l of stale) {
        next[l] = freshCounts();
        void db.setMeta(`counts:${l}`, next[l]);
      }
      return next;
    });
  }, [allCounts]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void doSync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [doSync]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    localStorage.setItem(LANG_KEY, next);
  }, []);

  const bumpCounts = useCallback(
    (kind: "new" | "review") => {
      setAllCounts((current) => {
        const existing = current[lang];
        const base = existing.date === today() ? existing : freshCounts();
        const next: DailyCounts = {
          ...base,
          reviewed: base.reviewed + 1,
          newIntroduced: base.newIntroduced + (kind === "new" ? 1 : 0),
        };
        void db.setMeta(`counts:${lang}`, next);
        return { ...current, [lang]: next };
      });
    },
    [lang],
  );

  const saveProfile = useCallback(async (target: Lang, profile: LangProfile) => {
    setProfiles((current) => ({ ...current, [target]: profile }));
    await sync.saveProfile(target, profile);
  }, []);

  const value = useMemo<StoreValue>(
    () => ({
      ...collection,
      ready,
      lang,
      setLang,
      profile: profiles[lang],
      profiles,
      saveProfile,
      counts: allCounts[lang],
      bumpCounts,
      reload,
      syncNow: doSync,
      syncing,
      syncError,
      pending,
      lastSync,
      online,
      locked,
      unlock,
    }),
    [collection, ready, lang, setLang, profiles, saveProfile, allCounts, bumpCounts, reload,
     doSync, syncing, syncError, pending, lastSync, online, locked, unlock],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore must be used inside a StoreProvider");
  return value;
}
